import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IconSpeakerMute } from '../components/Icons';
import { getRealtimeSseUrl } from '../config/adminApi';
import * as adminApi from '../api/adminApi';
import { normalizeListResponse } from '../lib/apiHelpers';
import { formatMoney } from '../lib/format';
import { orderDisplayNumber, orderRowId, pickRealtimeOrderId } from '../lib/orders';
import { showToast } from '../lib/toast';
import { useAuth } from './AuthContext';

const AdminRealtimeContext = createContext(null);
const SOUND_MUTED_KEY = 'adminSoundMuted';

/**
 * Backends emit new-order events under varying names; listen on all common
 * aliases so a name mismatch doesn't silently kill the toast/sound flow.
 */
const ORDER_EVENT_ALIASES = [
  'order:placed',
  'order.placed',
  'order:new',
  'order:created',
  'order_created',
  'order_placed',
  'new-order',
  'newOrder',
  'orderCreated',
  'orderPlaced',
];

function unwrapRealtimeOrder(payload) {
  if (!payload || typeof payload !== 'object') return {};
  const order = payload.order ?? payload.data?.order ?? payload.data ?? payload;
  if (!order || typeof order !== 'object') return {};
  return {
    ...order,
    orderId: order.orderId ?? payload.orderId ?? payload.data?.orderId,
    orderNumber: order.orderNumber ?? order.orderNo ?? payload.orderNumber ?? payload.orderNo,
  };
}

function getInitialSoundMuted() {
  if (typeof window === 'undefined') return false;
  return window.localStorage.getItem(SOUND_MUTED_KEY) === 'true';
}

function storeSoundMuted(value) {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(SOUND_MUTED_KEY, value ? 'true' : 'false');
}

/**
 * Server-Sent Events (SSE) connection for admin realtime: new-order toasts, sound, desktop alerts.
 * @param {{ children: React.ReactNode }} props
 */
export function AdminRealtimeProvider({ children }) {
  const { isAuthenticated, refreshSession } = useAuth();
  const [connectionState, setConnectionState] = useState('disconnected');
  const [soundMuted, setSoundMuted] = useState(getInitialSoundMuted);
  const [notification, setNotification] = useState(null);
  const [soundBlocked, setSoundBlocked] = useState(false);
  const eventSourceRef = useRef(null);
  const audioRef = useRef(null);
  const audioUnlockedRef = useRef(false);
  const soundMutedRef = useRef(soundMuted);
  const refreshAttemptRef = useRef(false);
  const orderPlacedListenersRef = useRef(new Set());
  const navigate = useNavigate();

  soundMutedRef.current = soundMuted;

  const sseUrl = useMemo(() => getRealtimeSseUrl(), []);
  const shouldConnect = isAuthenticated;

  useEffect(() => {
    if (!audioRef.current) {
      const audio = new Audio(`${process.env.PUBLIC_URL || ''}/sound.mp3`);
      audio.preload = 'auto';
      audio.volume = 1.0;
      audioRef.current = audio;
    }
  }, []);

  const waitForAudioReady = useCallback(async (audio) => {
    if (audio.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) return;
    await new Promise((resolve, reject) => {
      const onReady = () => {
        cleanup();
        resolve();
      };
      const onError = () => {
        cleanup();
        reject(new Error('Failed to load sound.mp3'));
      };
      const cleanup = () => {
        audio.removeEventListener('canplaythrough', onReady);
        audio.removeEventListener('error', onError);
      };
      audio.addEventListener('canplaythrough', onReady, { once: true });
      audio.addEventListener('error', onError, { once: true });
      audio.load();
    });
  }, []);

  /** Browsers block audio until a user gesture runs play() once. */
  const unlockAudio = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio || audioUnlockedRef.current) return audioUnlockedRef.current;
    try {
      await waitForAudioReady(audio);
      const prevVolume = audio.volume;
      audio.muted = true;
      audio.volume = 0;
      await audio.play();
      audio.pause();
      audio.currentTime = 0;
      audio.muted = false;
      audio.volume = prevVolume;
      audioUnlockedRef.current = true;
      setSoundBlocked(false);
      return true;
    } catch {
      return false;
    }
  }, [waitForAudioReady]);

  // Unlock audio & request notification permission on any user interaction
  useEffect(() => {
    const onGesture = () => {
      void unlockAudio();
      if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'default') {
        Notification.requestPermission().catch(() => {});
      }
    };
    window.addEventListener('pointerdown', onGesture, true);
    window.addEventListener('keydown', onGesture, true);
    return () => {
      window.removeEventListener('pointerdown', onGesture, true);
      window.removeEventListener('keydown', onGesture, true);
    };
  }, [unlockAudio]);

  const tryPlaySound = useCallback(async () => {
    if (soundMutedRef.current) return false;
    const audio = audioRef.current;
    if (!audio) return false;
    if (!audioUnlockedRef.current) {
      const unlocked = await unlockAudio();
      if (!unlocked) {
        setSoundBlocked(true);
        return false;
      }
    }
    try {
      await waitForAudioReady(audio);
      audio.pause();
      audio.currentTime = 0;
      audio.muted = false;
      audio.volume = 1;
      await audio.play();
      setSoundBlocked(false);
      return true;
    } catch {
      audioUnlockedRef.current = false;
      setSoundBlocked(true);
      return false;
    }
  }, [unlockAudio, waitForAudioReady]);

  const showOrderNotification = useCallback(
    (order) => {
      const safe = order && typeof order === 'object' ? order : {};
      const orderId = pickRealtimeOrderId(safe);
      const orderNumber = orderDisplayNumber(safe);
      const customerName = safe.customerName || safe.customer?.name || safe.user?.name || 'Customer';
      const totalNum = Number(safe.total ?? safe.totalAmount ?? safe.amount ?? 0);
      const title = `New order ${orderNumber}`;
      const message = `${customerName} — ${formatMoney(Number.isFinite(totalNum) ? totalNum : 0)}`;

      setNotification({
        id: `${orderId || orderNumber}-${Date.now()}`,
        orderId,
        orderNumber,
        rawOrder: safe,
        title,
        message,
      });

      // Show Desktop Notification if tab is in background or minimized
      if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'granted') {
        try {
          const desktopNotif = new Notification(title, {
            body: message,
            icon: `${process.env.PUBLIC_URL || ''}/favicon.ico`,
            tag: `order-${orderId || orderNumber}`,
            requireInteraction: true,
          });
          desktopNotif.onclick = () => {
            window.focus();
            if (orderId) {
              navigate(`/orders/${orderId}`);
            }
          };
        } catch {
          /* ignore desktop notification failure */
        }
      }
    },
    [navigate]
  );

  const handleViewOrder = useCallback(
    async (toast) => {
      if (!toast) return;
      setNotification(null);

      let orderId = pickRealtimeOrderId(toast.rawOrder) || toast.orderId || '';
      const orderNumber = toast.orderNumber && toast.orderNumber !== '—' ? toast.orderNumber : '';

      if (!orderId && orderNumber) {
        try {
          const raw = await adminApi.listActiveOrders({ orderNumberContains: orderNumber });
          const { items } = normalizeListResponse(raw);
          const match =
            items.find((row) => orderDisplayNumber(row) === orderNumber) ?? items[0];
          orderId = orderRowId(match);
        } catch {
          /* lookup failed — fall through to toast */
        }
      }

      if (orderId) {
        navigate(`/orders/${orderId}`);
        return;
      }

      showToast('Could not open that order. Check Active orders and try again.', 'warning');
    },
    [navigate]
  );

  const subscribeOrderPlaced = useCallback((listener) => {
    orderPlacedListenersRef.current.add(listener);
    return () => {
      orderPlacedListenersRef.current.delete(listener);
    };
  }, []);

  const handleOrderPlaced = useCallback(
    (_payload, _eventName) => {
      const order = unwrapRealtimeOrder(_payload);
      showOrderNotification(order);
      orderPlacedListenersRef.current.forEach((listener) => {
        try {
          listener(order);
        } catch {
          /* listener error — ignore */
        }
      });
      if (!soundMutedRef.current) {
        void tryPlaySound();
      }
    },
    [showOrderNotification, tryPlaySound]
  );

  const closeSSE = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
  }, []);

  const connectSSE = useCallback(() => {
    if (!shouldConnect) return;

    closeSSE();
    setConnectionState('connecting');

    try {
      const eventSource = new EventSource(sseUrl, {
        withCredentials: true,
      });

      eventSourceRef.current = eventSource;

      eventSource.onopen = () => {
        refreshAttemptRef.current = false;
        setConnectionState('connected');
      };

      // Heartbeat ping from server keeps state alive & verifies connection
      eventSource.addEventListener('ping', () => {
        setConnectionState('connected');
      });

      // Parse and process incoming order events
      const onOrderEvent = (event, eventName) => {
        try {
          const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
          handleOrderPlaced(data, eventName);
        } catch (err) {
          console.warn(`[admin-realtime] Failed to parse SSE event (${eventName}):`, err);
        }
      };

      ORDER_EVENT_ALIASES.forEach((eventName) => {
        eventSource.addEventListener(eventName, (event) => onOrderEvent(event, eventName));
      });

      eventSource.onmessage = (event) => {
        try {
          const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
          if (data && (data.order || data.orderId || data.orderNumber || data.status)) {
            handleOrderPlaced(data, 'message');
          }
        } catch {
          /* ignore non-JSON generic messages */
        }
      };

      eventSource.onerror = async () => {
        if (eventSource.readyState === EventSource.CLOSED) {
          setConnectionState('disconnected');
          // If disconnected unexpectedly, attempt a session refresh once and reconnect
          if (!refreshAttemptRef.current) {
            refreshAttemptRef.current = true;
            try {
              await refreshSession();
              connectSSE();
            } catch {
              /* refresh failed */
            }
          }
        } else {
          setConnectionState('reconnecting');
        }
      };

      return () => {
        eventSource.close();
      };
    } catch (err) {
      console.error('[admin-realtime] Failed to initialize SSE EventSource:', err);
      setConnectionState('disconnected');
    }
  }, [closeSSE, handleOrderPlaced, refreshSession, shouldConnect, sseUrl]);

  useEffect(() => {
    if (!shouldConnect) {
      closeSSE();
      setConnectionState('disconnected');
      return;
    }
    const cleanup = connectSSE();
    return () => {
      if (typeof cleanup === 'function') cleanup();
    };
  }, [connectSSE, closeSSE, shouldConnect]);

  // Tab visibility & Online/Offline auto-reconnect resilience
  useEffect(() => {
    if (!shouldConnect) return undefined;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const es = eventSourceRef.current;
        if (!es || es.readyState === EventSource.CLOSED) {
          connectSSE();
        }
      }
    };

    const handleOnline = () => {
      connectSSE();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('online', handleOnline);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', handleOnline);
    };
  }, [connectSSE, shouldConnect]);

  useEffect(() => {
    if (!notification) return undefined;
    const timer = window.setTimeout(() => setNotification(null), 8000);
    return () => window.clearTimeout(timer);
  }, [notification]);

  const toggleSoundMuted = useCallback(() => {
    setSoundMuted((current) => {
      const next = !current;
      storeSoundMuted(next);
      soundMutedRef.current = next;
      if (!next) {
        void unlockAudio().then((ok) => {
          if (ok) void tryPlaySound();
        });
      }
      return next;
    });
  }, [tryPlaySound, unlockAudio]);

  const enableSound = useCallback(async () => {
    setSoundBlocked(false);
    storeSoundMuted(false);
    setSoundMuted(false);
    soundMutedRef.current = false;
    await unlockAudio();
    await tryPlaySound();
  }, [tryPlaySound, unlockAudio]);

  const testOrderSound = useCallback(async () => {
    await enableSound();
  }, [enableSound]);

  const value = useMemo(
    () => ({
      connectionState,
      soundMuted,
      soundBlocked,
      toggleSoundMuted,
      enableSound,
      testOrderSound,
      notification,
      subscribeOrderPlaced,
    }),
    [connectionState, enableSound, notification, soundBlocked, soundMuted, subscribeOrderPlaced, testOrderSound, toggleSoundMuted]
  );

  return (
    <AdminRealtimeContext.Provider value={value}>
      {children}
      <OrderNotificationToast
        notification={notification}
        onClose={() => setNotification(null)}
        onView={handleViewOrder}
      />
      {soundBlocked ? (
        <div className="realtime-sound-blocked-toast">
          <div className="realtime-sound-blocked-text">
            Sound playback was blocked. Tap to enable audible order alerts.
          </div>
          <button type="button" className="realtime-sound-blocked-button" onClick={enableSound}>
            <IconSpeakerMute size={18} />
            <span>Enable sound</span>
          </button>
        </div>
      ) : null}
    </AdminRealtimeContext.Provider>
  );
}

function OrderNotificationToast({ notification, onClose, onView }) {
  if (!notification) return null;
  const canView =
    Boolean(notification.orderId) ||
    (notification.orderNumber && notification.orderNumber !== '—');
  return (
    <div className="realtime-toast alert alert-info shadow rounded-3 p-3">
      <div className="d-flex align-items-start gap-3">
        <div className="flex-grow-1">
          <div className="fw-semibold">{notification.title}</div>
          <div className="small text-secondary">{notification.message}</div>
        </div>
        <div className="d-flex flex-column align-items-end gap-2">
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onClose}>
            Close
          </button>
          <button
            type="button"
            className="btn btn-sm btn-primary"
            disabled={!canView}
            onClick={() => onView(notification)}
          >
            View
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * @returns {{ connectionState: string, soundMuted: boolean, setSoundMuted: (v: boolean) => void, notification: unknown, dismissNotification: () => void, subscribeOrderPlaced: (fn: (order: unknown) => void) => () => void }}
 */
export function useAdminRealtime() {
  const ctx = useContext(AdminRealtimeContext);
  if (!ctx) throw new Error('useAdminRealtime must be used within AdminRealtimeProvider');
  return ctx;
}
